import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PopupNames } from 'core';
import {
  useContributionMutations,
  useInvoiceMatching,
  usePaymentMutations,
  usePaymentCorrections,
  usePopupStateOperations,
} from 'core/hooks';
import { PATHS } from 'router/config';
import { MoneyCell } from 'molecules';
import { confirmModal } from '../../_detailShared/confirm';
import { formatMoneyCurrency } from '../../_detailShared/format';

import * as L from '../StudentDetail.styled';
import { AddContributionInlinePopup } from './AddContributionInlinePopup';
import { MarkPaidManuallyInlinePopup } from './MarkPaidManuallyInlinePopup';
import { PickContributionForPaymentPopup } from './PickContributionForPaymentPopup';
import { PickInvoiceForContributionPopup } from './PickInvoiceForContributionPopup';
import {
  CONTRIBUTION_STATUS_TONE,
  contributionStatusOf,
  isPaidContributionStatus,
} from './contributionStatus';
import { IAgreement, IContribution, IPayment, IStudent } from './types';

interface IPaymentsBlockProps {
  agreement: IAgreement;
  student: IStudent;
  studentId: string;
  studentEmail: string | null;
  onChanged?: () => void;
  hideAdd?: boolean;
}

type IContributionInvoice = NonNullable<
  NonNullable<IContribution['invoices']>[number]
>;

/** A Payment that covers (part of) a взнос — resolved for corrections. */
interface ICoveringPayment {
  id: string;
  label: string;
}

/** Tooltip label key for an invoice chip — mirrors `InvoiceStatusPill`. */
const invoiceStatusKey = (status?: string | null): string => {
  const upper = (status || '').toUpperCase();
  if (upper === 'PAID') return 'statusPaid';
  if (upper === 'PARTIAL') return 'statusPartial';
  if (upper === 'REJECTED') return 'statusRejected';
  if (upper === 'SENT') return 'statusSent';
  return 'statusIssued';
};

/**
 * Spec 0004 «Единая сущность „Взнос"»: schedule-first view where the взнос
 * (Contribution) is the only student money entity the operator sees. The
 * hidden `students.Payment` keeps powering the financial math server-side.
 *
 *   - Main table: one row per Contribution. The STATUS CHIP comes from the
 *     backend `status` field (derived from the linked Fakturownia invoices
 *     + hidden payments) — no client-side arithmetic. Columns «Оплачено» /
 *     «Остаток» mirror `coveredAmount` / `debt`.
 *   - Invoice chips: every linked invoice renders as a link to the invoice
 *     detail with the invoice status in the tooltip and an inline unlink.
 *   - Unpaid rows: «Привязать счёт» opens `PickInvoiceForContributionPopup`
 *     which now submits `linkInvoiceToContribution` (invoice → взнос link,
 *     no Payment creation) and «Отметить оплаченным вручную» opens the
 *     manual-mark popup (`markContributionPaidManually`).
 *   - Rows with `paidManually` offer «Отменить ручную оплату»
 *     (`revertManualContributionPayment`).
 *   - Corrections target a PAID взнос; the hidden Payment id is resolved
 *     underneath (`contribution.payment` or the linked invoices' payments;
 *     with several covering payments a secondary select appears).
 *
 *   - «Требуют привязки» migration block at the bottom — legacy orphan
 *     Payments (`payment.contribution == null`), unchanged.
 */
export const PaymentsBlock = memo(
  ({
    agreement,
    student,
    studentId,
    studentEmail,
    onChanged,
    hideAdd,
  }: IPaymentsBlockProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const {
      unlinkPayment,
      unlinkFromContribution,
      loading: linkLoading,
    } = useInvoiceMatching(studentId);
    const { deletePayment, loading: mutLoading } = usePaymentMutations(
      studentId,
    );
    const {
      revertManualPayment,
      loading: contribLoading,
    } = useContributionMutations(studentId);

    const [addingContribution, setAddingContribution] = useState(false);
    const [
      markingContribution,
      setMarkingContribution,
    ] = useState<IContribution | null>(null);
    const [
      linkingContribution,
      setLinkingContribution,
    ] = useState<IContribution | null>(null);
    const [migratingPayment, setMigratingPayment] = useState<IPayment | null>(
      null,
    );

    const contributions: IContribution[] = useMemo(
      () =>
        (agreement.contributions?.edges || [])
          .map((edge) => edge?.node)
          .filter((node): node is IContribution => Boolean(node)),
      [agreement.contributions],
    );

    const payments: IPayment[] = useMemo(
      () => (agreement.payments || []).filter((p): p is IPayment => Boolean(p)),
      [agreement.payments],
    );

    // Legacy data-fix surface: any Payment that doesn't close a
    // Contribution shouldn't exist in the new model. We surface it so the
    // operator can attach it without a separate admin tool.
    const orphanPayments = useMemo(
      () => payments.filter((p) => !p.contribution),
      [payments],
    );

    // Every Payment that covers this взнос — the manual/legacy one on the
    // contribution itself plus the auto-provisioned ones behind the linked
    // invoices. Used to resolve the correction target (backend contract is
    // still paymentId).
    const coveringPaymentsOf = useCallback(
      (c: IContribution): ICoveringPayment[] => {
        const out: ICoveringPayment[] = [];
        const seen = new Set<string>();
        if (c.payment) {
          seen.add(c.payment.id);
          out.push({
            id: c.payment.id,
            label: t('pages.studentDetail.correction.coverManual', {
              date: c.payment.date
                ? moment(c.payment.date).format('DD.MM.YYYY')
                : '—',
              amount: c.payment.amountAgreement,
            }),
          });
        }
        (c.invoices || []).forEach((inv) => {
          const p = inv?.payment;
          if (!p || seen.has(p.id)) return;
          seen.add(p.id);
          out.push({ id: p.id, label: inv!.number || `#${inv!.pk}` });
        });
        return out;
      },
      [t],
    );

    // ── Corrections (корректировки) ──────────────────────────────────────
    const {
      issue,
      remove: removeCorrection,
      setTransferOut,
      loading: corrLoading,
    } = usePaymentCorrections(studentId);

    const onToggleTransferOut = useCallback(
      async (value: boolean) => {
        const ok = await setTransferOut(agreement.id, value);
        if (ok) onChanged?.();
      },
      [setTransferOut, agreement.id, onChanged],
    );
    const corrections = useMemo(
      () => (agreement.corrections || []).filter(Boolean),
      [agreement.corrections],
    );
    const correctionsTotal = useMemo(
      () => corrections.reduce((s, c) => s + Number(c!.amountForeign || 0), 0),
      [corrections],
    );
    const [issuing, setIssuing] = useState(false);
    const [corrAmount, setCorrAmount] = useState('');
    const [corrReason, setCorrReason] = useState('');
    // '' = whole agreement; otherwise the id of a PAID взнос.
    const [corrContribution, setCorrContribution] = useState('');
    // The resolved hidden Payment (backend contract is still paymentId).
    const [corrPayment, setCorrPayment] = useState('');

    const paidContributions = useMemo(
      () =>
        contributions.filter((c) =>
          isPaidContributionStatus(contributionStatusOf(c)),
        ),
      [contributions],
    );

    const corrTargetContribution = useMemo(
      () => paidContributions.find((c) => c.id === corrContribution) || null,
      [paidContributions, corrContribution],
    );
    const corrCoveringPayments = useMemo(
      () =>
        corrTargetContribution
          ? coveringPaymentsOf(corrTargetContribution)
          : [],
      [corrTargetContribution, coveringPaymentsOf],
    );

    const resetCorrForm = useCallback(() => {
      setIssuing(false);
      setCorrAmount('');
      setCorrReason('');
      setCorrContribution('');
      setCorrPayment('');
    }, []);

    const pickCorrContribution = useCallback(
      (contribution: IContribution | null) => {
        if (!contribution) {
          setCorrContribution('');
          setCorrPayment('');
          return;
        }
        setCorrContribution(contribution.id);
        const covering = coveringPaymentsOf(contribution);
        setCorrPayment(covering.length === 1 ? covering[0].id : '');
      },
      [coveringPaymentsOf],
    );

    // Open the correction form pre-targeted to one paid взнос.
    const onStartCorrection = useCallback(
      (contribution: IContribution) => {
        pickCorrContribution(contribution);
        setCorrAmount('');
        setCorrReason('');
        setIssuing(true);
      },
      [pickCorrContribution],
    );

    const onIssueCorrection = useCallback(async () => {
      const amount = parseFloat(corrAmount);
      if (!amount || amount <= 0 || !corrReason.trim()) return;
      // A payment correction needs the resolved hidden Payment.
      if (corrContribution && !corrPayment) return;
      // Echo the exact amount + target + financial effect: a payment-correction
      // is a REFUND that INCREASES the debt; a plan-correction reduces it.
      const effect = corrContribution
        ? t('pages.studentDetail.correction.confirmRefund')
        : t('pages.studentDetail.correction.confirmPlan');
      const confirmed = await confirmModal(
        t('pages.studentDetail.correction.confirm', {
          amount: formatMoneyCurrency(amount, agreement.currency),
          effect,
        }),
      );
      if (!confirmed) return;
      const ok = await issue({
        agreementId: agreement.id,
        paymentId: corrContribution ? corrPayment : null,
        amount,
        currency: agreement.currency || '',
        date: moment().format('YYYY-MM-DD'),
        reason: corrReason.trim(),
      });
      if (ok) {
        resetCorrForm();
        onChanged?.();
      }
    }, [
      corrAmount,
      corrReason,
      corrContribution,
      corrPayment,
      issue,
      agreement.id,
      agreement.currency,
      onChanged,
      resetCorrForm,
      t,
    ]);

    const onRemoveCorrection = useCallback(
      async (id: string) => {
        const ok = await removeCorrection(id);
        if (ok) onChanged?.();
      },
      [removeCorrection, onChanged],
    );

    const onDeletePaidContribution = useCallback(
      (c: IContribution) => {
        const { payment } = c;
        if (!payment) return;
        const dateStr = moment(payment.date).format('DD.MM.YYYY');
        const amount = `${payment.amountAgreement} ${
          payment.currency || agreement.currency || ''
        }`.trim();
        const invoice = payment.fakturowniaInvoice;
        const invoiceLabel =
          invoice?.number || (invoice ? `#${invoice.pk}` : '');
        const title = t(
          'pages.studentDetail.payments.schedule.deletePaymentTitle',
        );
        const body = invoice
          ? t(
              'pages.studentDetail.payments.schedule.deletePaymentWithInvoiceBody',
              { date: dateStr, amount, invoice: invoiceLabel },
            )
          : t('pages.studentDetail.payments.schedule.deletePaymentBody', {
              date: dateStr,
              amount,
            });
        showPopup(PopupNames.zoomDangerConfirm, {
          title,
          body,
          confirmString: dateStr,
          onConfirm: async () => {
            // Backend forbids deletePayment while an invoice is linked.
            // Unlink first, then delete; backend then clears
            // contribution.payment on delete.
            if (invoice) {
              const unlinked = await unlinkPayment(invoice.id);
              if (!unlinked) return;
            }
            const ok = await deletePayment(payment.id);
            if (ok && onChanged) onChanged();
          },
        });
      },
      [
        showPopup,
        unlinkPayment,
        deletePayment,
        onChanged,
        t,
        agreement.currency,
      ],
    );

    // Undo the manual «оплачен вручную» mark (deletes the hidden manual
    // Payment server-side).
    const onRevertManual = useCallback(
      async (c: IContribution) => {
        const confirmed = await confirmModal(
          t('pages.studentDetail.payments.schedule.revertManualConfirm', {
            date: moment(c.date).format('DD.MM.YYYY'),
          }),
        );
        if (!confirmed) return;
        const ok = await revertManualPayment(c.id);
        if (ok) onChanged?.();
      },
      [revertManualPayment, onChanged, t],
    );

    const onUnlinkInvoice = useCallback(
      async (invoice: IContributionInvoice) => {
        const confirmed = await confirmModal(
          t('pages.studentDetail.payments.schedule.unlinkInvoiceConfirm', {
            number: invoice.number || `#${invoice.pk}`,
          }),
        );
        if (!confirmed) return;
        const ok = await unlinkFromContribution(invoice.id);
        if (ok) onChanged?.();
      },
      [unlinkFromContribution, onChanged, t],
    );

    const onDeleteOrphanPayment = useCallback(
      (p: IPayment) => {
        if (p.fakturowniaInvoice) return;
        const dateStr = moment(p.date).format('DD.MM.YYYY');
        const amount = `${p.amountAgreement} ${
          p.currency || agreement.currency || ''
        }`.trim();
        showPopup(PopupNames.zoomDangerConfirm, {
          title: t('pages.studentDetail.payments.dangerTitle'),
          body: t('pages.studentDetail.payments.dangerBody', {
            date: dateStr,
            amount,
          }),
          confirmString: dateStr,
          onConfirm: async () => {
            const ok = await deletePayment(p.id);
            if (ok && onChanged) onChanged();
          },
        });
      },
      [showPopup, deletePayment, onChanged, t, agreement.currency],
    );

    const openInvoiceDetail = useCallback(
      (invoiceId: string) =>
        history.push(PATHS.financesInvoice.replace(':id', invoiceId)),
      [history],
    );

    const actionsBusy = mutLoading || linkLoading || contribLoading;

    return (
      <L.Subblock>
        {/* ─────────── SCHEDULE ─────────── */}
        <L.SubblockHeader>
          {t('pages.studentDetail.payments.schedule.title')}
          <L.SubblockHeaderCount>
            · {contributions.length}
          </L.SubblockHeaderCount>
          {!hideAdd ? (
            <L.SubblockRight>
              <L.PrimaryActionButton
                type="button"
                onClick={() => setAddingContribution(true)}
                disabled={mutLoading}
              >
                {t('pages.studentDetail.payments.addContribution.button')}
              </L.PrimaryActionButton>
            </L.SubblockRight>
          ) : null}
        </L.SubblockHeader>

        {contributions.length === 0 ? (
          <L.Muted>
            {t('pages.studentDetail.payments.schedule.emptyNoSchedule')}
          </L.Muted>
        ) : (
          <L.SmallTable>
            <thead>
              <tr>
                <th>{t('pages.studentDetail.payments.schedule.colDate')}</th>
                <th style={{ textAlign: 'right' }}>
                  {t('pages.studentDetail.payments.schedule.colPlan')}
                </th>
                <th>{t('pages.studentDetail.payments.schedule.colStatus')}</th>
                <th style={{ textAlign: 'right' }}>
                  {t('pages.studentDetail.payments.schedule.colPaid')}
                </th>
                <th style={{ textAlign: 'right' }}>
                  {t('pages.studentDetail.payments.schedule.colDebt')}
                </th>
                <th>{t('pages.studentDetail.payments.schedule.colInvoice')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {contributions.map((c) => {
                const status = contributionStatusOf(c);
                const isPaid = isPaidContributionStatus(status);
                const invoiceChips = (
                  c.invoices || []
                ).filter((inv): inv is IContributionInvoice => Boolean(inv));
                // Pre-0004 rows: the invoice may still hang off the hidden
                // Payment only — show it (without unlink) so nothing vanishes.
                const legacyInvoice =
                  invoiceChips.length === 0
                    ? c.payment?.fakturowniaInvoice
                    : null;
                const debt = c.debt ?? c.remainder ?? 0;
                return (
                  <tr key={c.id}>
                    <td>{moment(c.date).format('DD.MM.YYYY')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <MoneyCell
                        amount={c.amountForeign}
                        currency={agreement.currency || ''}
                      />
                    </td>
                    <td>
                      <L.Tag tone={CONTRIBUTION_STATUS_TONE[status]}>
                        {t(
                          `pages.studentDetail.payments.schedule.status.${status}`,
                        )}
                      </L.Tag>
                      {status === 'overdue' && c.delay ? (
                        <L.Muted style={{ marginLeft: 6 }}>
                          {t(
                            'pages.studentDetail.payments.schedule.delayDays',
                            {
                              count: c.delay,
                            },
                          )}
                        </L.Muted>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <MoneyCell
                        amount={c.coveredAmount ?? 0}
                        currency={agreement.currency || ''}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <MoneyCell
                        amount={debt}
                        currency={agreement.currency || ''}
                        highlightOutstanding
                      />
                    </td>
                    <td>
                      {invoiceChips.length === 0 && !legacyInvoice ? (
                        <L.Muted>—</L.Muted>
                      ) : (
                        <>
                          {invoiceChips.map((inv) => (
                            <span
                              key={inv.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 2,
                                marginRight: 8,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <L.PaymentLink
                                href={PATHS.financesInvoice.replace(
                                  ':id',
                                  inv.id,
                                )}
                                title={t(
                                  `pages.fakturownia.invoices.filters.${invoiceStatusKey(
                                    inv.status,
                                  )}`,
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  openInvoiceDetail(inv.id);
                                }}
                              >
                                {inv.number || `#${inv.pk}`}
                              </L.PaymentLink>
                              <button
                                type="button"
                                title={t(
                                  'pages.studentDetail.payments.schedule.unlinkInvoice',
                                )}
                                onClick={() => onUnlinkInvoice(inv)}
                                disabled={actionsBusy}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: '#94a3b8',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: 11,
                                }}
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                          {legacyInvoice ? (
                            <L.PaymentLink
                              href={PATHS.financesInvoice.replace(
                                ':id',
                                legacyInvoice.id,
                              )}
                              title={t(
                                `pages.fakturownia.invoices.filters.${invoiceStatusKey(
                                  legacyInvoice.status,
                                )}`,
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                openInvoiceDetail(legacyInvoice.id);
                              }}
                            >
                              {legacyInvoice.number || `#${legacyInvoice.pk}`}
                            </L.PaymentLink>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        gap: 4,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {isPaid ? (
                        <>
                          {c.paidManually ? (
                            <L.PrimaryActionButton
                              type="button"
                              onClick={() => onRevertManual(c)}
                              disabled={actionsBusy}
                            >
                              {t(
                                'pages.studentDetail.payments.schedule.revertManualAction',
                              )}
                            </L.PrimaryActionButton>
                          ) : c.payment ? (
                            <L.PrimaryActionButton
                              type="button"
                              onClick={() => onDeletePaidContribution(c)}
                              disabled={actionsBusy}
                            >
                              {t(
                                'pages.studentDetail.payments.schedule.deletePayment',
                              )}
                            </L.PrimaryActionButton>
                          ) : null}
                          <L.PrimaryActionButton
                            type="button"
                            onClick={() => onStartCorrection(c)}
                            disabled={corrLoading}
                          >
                            {t('pages.studentDetail.correction.add')}
                          </L.PrimaryActionButton>
                        </>
                      ) : (
                        <>
                          <L.PrimaryActionButton
                            type="button"
                            tone="primary"
                            onClick={() => setLinkingContribution(c)}
                            disabled={actionsBusy}
                          >
                            {t(
                              'pages.studentDetail.payments.schedule.linkInvoice',
                            )}
                          </L.PrimaryActionButton>
                          <L.PrimaryActionButton
                            type="button"
                            onClick={() => setMarkingContribution(c)}
                            disabled={actionsBusy}
                          >
                            {t(
                              'pages.studentDetail.payments.schedule.markManualAction',
                            )}
                          </L.PrimaryActionButton>
                          {c.payment ? (
                            <L.PrimaryActionButton
                              type="button"
                              onClick={() => onDeletePaidContribution(c)}
                              disabled={actionsBusy}
                            >
                              {t(
                                'pages.studentDetail.payments.schedule.deletePayment',
                              )}
                            </L.PrimaryActionButton>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </L.SmallTable>
        )}

        {/* ─────────── MIGRATION ─────────── */}
        {orphanPayments.length > 0 ? (
          <>
            <L.SubblockHeader style={{ marginTop: 20 }}>
              {t('pages.studentDetail.payments.migration.title')}
              <L.SubblockHeaderCount>
                · {orphanPayments.length}
              </L.SubblockHeaderCount>
            </L.SubblockHeader>
            <L.Muted style={{ marginBottom: 8 }}>
              {t('pages.studentDetail.payments.migration.hint')}
            </L.Muted>
            <L.SmallTable>
              <thead>
                <tr>
                  <th>{t('pages.studentDetail.payments.date')}</th>
                  <th style={{ textAlign: 'right' }}>
                    {t('pages.studentDetail.payments.amount')}
                  </th>
                  <th>{t('pages.studentDetail.payments.linkedInvoice')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {orphanPayments.map((p) => {
                  const inv = p.fakturowniaInvoice;
                  return (
                    <L.OrphanRowTone key={p.id}>
                      <td>{moment(p.date).format('DD.MM.YYYY')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <MoneyCell
                          amount={p.amountAgreement}
                          currency={p.currency || agreement.currency || ''}
                        />
                      </td>
                      <td>
                        {inv ? (
                          <L.PaymentLink
                            href={PATHS.financesInvoice.replace(':id', inv.id)}
                            onClick={(e) => {
                              e.preventDefault();
                              openInvoiceDetail(inv.id);
                            }}
                          >
                            {inv.number || `#${inv.pk}`}
                          </L.PaymentLink>
                        ) : (
                          <L.Muted>—</L.Muted>
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          gap: 4,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <L.PrimaryActionButton
                          type="button"
                          tone="primary"
                          onClick={() => setMigratingPayment(p)}
                          disabled={mutLoading}
                        >
                          {t(
                            'pages.studentDetail.payments.migration.linkAction',
                          )}
                        </L.PrimaryActionButton>
                        {!inv ? (
                          <L.PrimaryActionButton
                            type="button"
                            onClick={() => onDeleteOrphanPayment(p)}
                            disabled={mutLoading}
                          >
                            {t(
                              'pages.studentDetail.payments.migration.deleteAction',
                            )}
                          </L.PrimaryActionButton>
                        ) : null}
                      </td>
                    </L.OrphanRowTone>
                  );
                })}
              </tbody>
            </L.SmallTable>
          </>
        ) : null}

        {/* ─────────── CORRECTIONS (корректировки) ─────────── */}
        <div style={{ marginTop: 16 }}>
          <label
            htmlFor="agreement-transfer-out"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: '#475569',
              marginBottom: 8,
            }}
          >
            <input
              id="agreement-transfer-out"
              type="checkbox"
              checked={Boolean(agreement.transferOut)}
              disabled={corrLoading}
              onChange={(e) => onToggleTransferOut(e.target.checked)}
            />
            {t('pages.studentDetail.correction.transferOut')}
          </label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
              {t('pages.studentDetail.correction.title')}
              {corrections.length
                ? ` · ${corrections.length} · −${formatMoneyCurrency(
                    correctionsTotal,
                    agreement.currency,
                  )}`
                : ''}
            </div>
            {!issuing ? (
              <button
                type="button"
                onClick={() => setIssuing(true)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#7c3aed',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                + {t('pages.studentDetail.correction.add')}
              </button>
            ) : null}
          </div>

          {corrections.map((c) => (
            <div
              key={c!.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 0',
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600, color: '#dc2626' }}>
                −{' '}
                {formatMoneyCurrency(
                  c!.amountForeign,
                  c!.currency || agreement.currency,
                )}
              </span>
              <span style={{ color: '#334155' }}>{c!.reason}</span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>
                {c!.payment
                  ? t('pages.studentDetail.correction.toPayment')
                  : t('pages.studentDetail.correction.toAgreement')}
                {c!.date ? ` · ${moment(c!.date).format('DD.MM.YYYY')}` : ''}
              </span>
              <button
                type="button"
                onClick={() => onRemoveCorrection(c!.id)}
                disabled={corrLoading}
                style={{
                  marginLeft: 'auto',
                  border: 'none',
                  background: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          ))}

          {issuing ? (
            <div
              style={{
                marginTop: 8,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <select
                value={corrContribution}
                onChange={(e) =>
                  pickCorrContribution(
                    paidContributions.find((c) => c.id === e.target.value) ||
                      null,
                  )
                }
                style={{ padding: '4px 6px', fontSize: 13 }}
              >
                <option value="">
                  {t('pages.studentDetail.correction.targetAgreement')}
                </option>
                {paidContributions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {t('pages.studentDetail.correction.targetContribution', {
                      date: c.date ? moment(c.date).format('DD.MM.YYYY') : '',
                      amount: c.amountForeign ?? '—',
                    })}
                  </option>
                ))}
              </select>
              {corrContribution && corrCoveringPayments.length > 1 ? (
                <select
                  value={corrPayment}
                  onChange={(e) => setCorrPayment(e.target.value)}
                  style={{ padding: '4px 6px', fontSize: 13 }}
                >
                  <option value="">
                    {t('pages.studentDetail.correction.pickPayment')}
                  </option>
                  {corrCoveringPayments.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {corrContribution && corrCoveringPayments.length === 0 ? (
                <div
                  style={{ flexBasis: '100%', fontSize: 11, color: '#b91c1c' }}
                >
                  {t('pages.studentDetail.correction.noCoveringPayment')}
                </div>
              ) : null}
              {corrContribution ? (
                <div
                  style={{ flexBasis: '100%', fontSize: 11, color: '#b45309' }}
                >
                  {t('pages.studentDetail.correction.refundHint')}
                </div>
              ) : null}
              <input
                type="number"
                value={corrAmount}
                onChange={(e) => setCorrAmount(e.target.value)}
                placeholder={t('pages.studentDetail.correction.amount')}
                style={{ width: 110, padding: '4px 6px', fontSize: 13 }}
              />
              <input
                value={corrReason}
                onChange={(e) => setCorrReason(e.target.value)}
                placeholder={t('pages.studentDetail.correction.reason')}
                style={{
                  flex: 1,
                  minWidth: 140,
                  padding: '4px 6px',
                  fontSize: 13,
                }}
              />
              <button
                type="button"
                onClick={onIssueCorrection}
                disabled={
                  corrLoading || Boolean(corrContribution && !corrPayment)
                }
                style={{
                  border: '1px solid #7c3aed',
                  background: corrLoading ? '#ede9fe' : '#7c3aed',
                  color: corrLoading ? '#7c3aed' : '#fff',
                  padding: '5px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: corrLoading ? 'default' : 'pointer',
                }}
              >
                {t('pages.studentDetail.correction.issue')}
              </button>
              <button
                type="button"
                onClick={resetCorrForm}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#64748b',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t('pages.studentDetail.correction.cancel')}
              </button>
            </div>
          ) : null}
        </div>

        {/* ─────────── POPUPS ─────────── */}
        {addingContribution ? (
          <AddContributionInlinePopup
            student={student}
            agreement={agreement}
            studentId={studentId}
            onClose={() => setAddingContribution(false)}
            onSaved={onChanged}
          />
        ) : null}
        {markingContribution ? (
          <MarkPaidManuallyInlinePopup
            contribution={markingContribution}
            agreement={agreement}
            studentId={studentId}
            onClose={() => setMarkingContribution(null)}
            onSaved={onChanged}
          />
        ) : null}
        {linkingContribution ? (
          <PickInvoiceForContributionPopup
            contribution={linkingContribution}
            agreement={agreement}
            studentId={studentId}
            studentEmail={studentEmail}
            onClose={() => setLinkingContribution(null)}
            onLinked={onChanged}
          />
        ) : null}
        {migratingPayment ? (
          <PickContributionForPaymentPopup
            payment={migratingPayment}
            agreement={agreement}
            studentId={studentId}
            onClose={() => setMigratingPayment(null)}
            onLinked={onChanged}
          />
        ) : null}
      </L.Subblock>
    );
  },
);
